import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'
import type {
  CreatorSummary,
  FavoriteEntry,
  ListSort,
  PostListPage,
  PostSearchPage,
  PostSearchParams,
  PublicFeaturedTag,
  PublicPostDetail,
  PublicTag,
  PublicTagDetail,
  TagCatalogPage,
  TagCategory,
} from '@/types/api'
import * as catalogApi from './catalog'
import * as engageApi from './engage'
import * as postsApi from './posts'
import { queryKeys } from './queryClient'
import { groupTagsByCategory, type TagGroup } from './taxonomy'

/**
 * 页面用到的查询与变更 hooks。
 *
 * 组件只跟这一层打交道，既不碰 `fetch`（AGENTS.md 2 明令禁止），也不必知道
 * 查询键长什么样。所有「写成功之后该让哪些缓存失效」的规则都写在这里，
 * 而不是散落在各个按钮的 onClick 里。
 */

/* ------------------------------------------------------------------ 查询 */

export function useSearchPostsQuery(
  params: PostSearchParams,
  options: { enabled?: boolean } = {},
): UseQueryResult<PostSearchPage> {
  const normalized = postsApi.normalizeSearchParams(params)
  return useQuery({
    queryKey: queryKeys.posts.search(normalized as Record<string, unknown>),
    queryFn: ({ signal }) => postsApi.searchPosts(normalized, signal),
    enabled: options.enabled ?? true,
  })
}

export function usePostDetailQuery(slug: string): UseQueryResult<PublicPostDetail> {
  return useQuery({
    queryKey: queryKeys.posts.detail(slug),
    queryFn: ({ signal }) => postsApi.fetchPostDetail(slug, signal),
    enabled: Boolean(slug),
  })
}

/**
 * 标签列表。省略 `category` 时三栏都要。
 *
 * `limit` 顶到后端上限 100：这个接口**没有分页**（只有 `q` / `category` /
 * `limit`），所以 100 就是前端一次能拿到的全部。标签超过 100 个时会被截断，
 * 见 docs/backend-sync/2026-08-08-backend-contract-update.md 的「仍然缺失」一节。
 */
export function useTagsQuery(
  params: { q?: string; category?: TagCategory } = {},
): UseQueryResult<PublicTag[]> {
  const query = {
    ...(params.q ? { q: params.q } : {}),
    ...(params.category ? { category: params.category } : {}),
    limit: 100,
  }
  return useQuery({
    queryKey: queryKeys.tags.list(query),
    queryFn: ({ signal }) => catalogApi.fetchTags(query, signal),
    // 标签列表几乎不变，缓存久一点；它被侧栏导航、首页磁贴与筛选面板同时使用。
    staleTime: 10 * 60_000,
  })
}

/**
 * 按栏目分好组的标签（类型 / 游戏 / 其他）。
 *
 * 侧栏导航、首页磁贴与筛选面板共用**同一次** `GET /tags` —— 它们要的都是全部
 * 标签，分三次按 category 取只会多打两个请求，缓存也更碎。
 */
export function useTagGroups(): { groups: TagGroup[]; isPending: boolean } {
  const tagsQuery = useTagsQuery()
  const tags = tagsQuery.data
  const groups = useMemo(() => (tags ? groupTagsByCategory(tags) : []), [tags])
  return { groups, isPending: tagsQuery.isPending && tags === undefined }
}

/**
 * 首页展示位。运营指定的那几格，带封面与次序。
 *
 * 与 `useTagsQuery` 是两个不同的东西，不要用其中一个顶替另一个：这里是
 * `GET /featured-tags`（`featured_tags` 表，运营配置），那里是全站标签清单。
 *
 * 缓存与标签列表同样给到 10 分钟：这是一份人工维护的配置，一天改不了几次。
 * 管理端改完会显式失效 `queryKeys.tags.all`（展示位键就在它下面），所以
 * 「改了却要等 10 分钟才看到」不会发生。
 */
export function useFeaturedTagsQuery(
  params: { limit?: number } = {},
): UseQueryResult<PublicFeaturedTag[]> {
  const query = { ...(params.limit ? { limit: params.limit } : {}) }
  return useQuery({
    queryKey: queryKeys.tags.featured(query),
    queryFn: ({ signal }) => catalogApi.fetchFeaturedTags(query, signal),
    staleTime: 10 * 60_000,
  })
}

/**
 * `public_id` → 标签。
 *
 * 用途是把 URL 里的 `?tag_id=` 翻译回「游戏 › Minecraft」这样的文案 ——
 * 后端**没有** `GET /tags/{public_id}` 单条查询，只能从列表里找。
 * 找不到时调用方应原样显示 id，不要假装不存在。
 */
export function useTagLookup(): Map<string, PublicTag> {
  const tagsQuery = useTagsQuery()
  const tags = tagsQuery.data
  return useMemo(() => new Map((tags ?? []).map((tag) => [tag.public_id, tag])), [tags])
}

/**
 * 「标签大全」的一页。
 *
 * 缓存与 `useTagsQuery` 一样给到 10 分钟：作品数变化很慢，而用户在这个页面上
 * 会来回翻页与改搜索词，每次都重新请求只会让翻页看起来在闪。
 */
export function useTagCatalogQuery(
  params: { q?: string; category?: TagCategory; page?: number; limit?: number } = {},
): UseQueryResult<TagCatalogPage> {
  const query = {
    ...(params.q ? { q: params.q } : {}),
    ...(params.category ? { category: params.category } : {}),
    ...(params.page && params.page > 1 ? { page: params.page } : {}),
    ...(params.limit ? { limit: params.limit } : {}),
  }
  return useQuery({
    queryKey: queryKeys.tags.catalog(query),
    queryFn: ({ signal }) => catalogApi.fetchTagCatalog(query, signal),
    staleTime: 10 * 60_000,
    // 翻页时保留上一页的数据，否则整页会先塌成骨架屏再长回来。
    placeholderData: (previous) => previous,
  })
}

/**
 * 单个标签。标签落地页用它拿标题与作品数。
 *
 * 与 `useTagLookup` 是两回事：那个是从「最多 100 条」的列表里反查，查不到就只能
 * 显示「标签」；这个是后端的单条查询，任何一个标签都查得到。
 */
export function useTagQuery(publicId: string): UseQueryResult<PublicTagDetail> {
  return useQuery({
    queryKey: queryKeys.tags.detail(publicId),
    queryFn: ({ signal }) => catalogApi.fetchTag(publicId, signal),
    enabled: Boolean(publicId),
    staleTime: 10 * 60_000,
  })
}

/** 某个标签下的已发布内容，走后端专用的 `GET /tags/{public_id}/posts`。 */
export function useTagPostsQuery(
  publicId: string,
  params: { cursor?: string; limit?: number; sort?: ListSort } = {},
): UseQueryResult<PostListPage> {
  return useQuery({
    queryKey: queryKeys.tags.posts(publicId, params),
    queryFn: ({ signal }) => postsApi.fetchTagPosts(publicId, params, signal),
    enabled: Boolean(publicId),
  })
}

/**
 * 作者列表。搜索页「作者」筛选用它填 chips。
 *
 * 与 `useTagsQuery` 一样缓存久一点：作者名单变化很慢，而筛选面板在桌面端是常驻的。
 * 后端这个接口没有分页也没有排序，只有 `q` 与 `limit`（最大 100）。
 */
export function useCreatorsQuery(
  q?: string,
  options: { enabled?: boolean } = {},
): UseQueryResult<CreatorSummary[]> {
  return useQuery({
    queryKey: queryKeys.creators.list(q),
    queryFn: ({ signal }) => catalogApi.fetchCreators({ ...(q ? { q } : {}), limit: 100 }, signal),
    staleTime: 10 * 60_000,
    // 发布页只有管理员那一版需要作者名单；普通访客不该为它多发一次请求。
    enabled: options.enabled ?? true,
  })
}

export function useCreatorQuery(publicId: string): UseQueryResult<CreatorSummary> {
  return useQuery({
    queryKey: queryKeys.creators.detail(publicId),
    queryFn: ({ signal }) => catalogApi.fetchCreator(publicId, signal),
    enabled: Boolean(publicId),
  })
}

export function useCreatorPostsQuery(
  publicId: string,
  params: { cursor?: string; limit?: number; sort?: ListSort } = {},
): UseQueryResult<PostListPage> {
  return useQuery({
    queryKey: queryKeys.creators.posts(publicId, params),
    queryFn: ({ signal }) => postsApi.fetchCreatorPosts(publicId, params, signal),
    enabled: Boolean(publicId),
  })
}

export function useFavoritesQuery(
  params: { limit?: number; before?: string } = {},
  options: { enabled?: boolean } = {},
): UseQueryResult<FavoriteEntry[]> {
  return useQuery({
    queryKey: queryKeys.favorites.list(params),
    queryFn: ({ signal }) => engageApi.fetchFavorites(params, signal),
    enabled: options.enabled ?? true,
    // 收藏列表是 `no-store` 的私有数据，不要长时间留在缓存里。
    staleTime: 0,
  })
}

/* ------------------------------------------------------------------ 变更 */

/**
 * 收藏 / 取消收藏。
 *
 * 做乐观更新：收藏是高频、低风险的操作，等一个往返再变心形会显得很迟钝。
 * 失败时回滚到操作前的快照，并让详情缓存失效以拿回权威值 —— 因为
 * `favorite_count` 是后端算的，本地 ±1 只是猜测。
 */
export function useToggleFavoriteMutation(slug: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (next: boolean) =>
      next ? engageApi.addFavorite(slug) : engageApi.removeFavorite(slug),
    onMutate: async (next: boolean) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.posts.detail(slug) })
      const previous = queryClient.getQueryData<PublicPostDetail>(queryKeys.posts.detail(slug))
      if (previous) {
        queryClient.setQueryData<PublicPostDetail>(queryKeys.posts.detail(slug), {
          ...previous,
          favorited: next,
          favorite_count: Math.max(0, previous.favorite_count + (next ? 1 : -1)),
        })
      }
      return { previous }
    },
    onError: (_error, _next, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKeys.posts.detail(slug), context.previous)
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.posts.detail(slug) })
      void queryClient.invalidateQueries({ queryKey: queryKeys.favorites.all })
    },
  })
}

/**
 * 登记一次阅读。
 *
 * 刻意**不**失效详情缓存：后端按 (post, 匿名 ID, UTC 日) 去重，`counted` 为
 * true 也只是让计数 +1，为这个数字重取整个详情不值得。用户下次进来自然会看到
 * 新的数。
 */
export function useRecordViewMutation() {
  return useMutation({
    mutationFn: (slug: string) => engageApi.recordView(slug),
  })
}
